
/**
 * ELAKAY – GraffitiOrderSeller.jsx
 * 
 * Version sans modal, fluide, responsive — utilise le design system du GraffitiConfigurator
 */

import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { DesignPreviewModal } from "./externPageGraffiti/GraffitiPreview";
import DesignStudio, { makeSlotDesign, CSS_ } from "./externPageGraffiti/DesignStudio";
import useGraphicOrderSave from "./externPageGraffiti/useGraphicOrderSave";
import { isAuthenticated } from "../../auth";
import ShowimagesLoading from "../showImagesLoading";
import { elakayAutoText } from "../dataElakay";


let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["graffitiOrderSeller"]?.[key] || fallback;

const getHardcodedText = (infoSource, group, key, fallback) =>
  infoSource?.laguangeElakay?.("frontElakayHardcodedText", infoSource.languageAPI)?.[group]?.[key] || fallback;

/* ══════════════════════════════════════════════════════
   CSS — Style du GraffitiConfigurator (clair, cyan/teal)
══════════════════════════════════════════════════════ */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&family=Space+Mono:wght@400;700&display=swap');

*,*::before,*::after{box-sizing:border-box}

.gos-root{
  --n0:#f2f6fd;
  --n1:#ffffff;
  --n2:#f4f8fc;
  --n3:#eef6fb;
  --n4:#daeaf5;
  --n5:#c8ddea;
  --primary:#007399;
  --primary-light:#00a4d8;
  --primary-soft:rgba(0,115,153,.10);
  --primary-softer:rgba(0,115,153,.05);
  --teal:#4facfe;
  --teal-light:#00f2fe;
  --teal-soft:rgba(79,172,254,.10);
  --emerald:#34c759;
  --emerald-soft:rgba(52,199,89,.08);
  --gold:#c9a227;
  --gold-soft:rgba(201,162,39,.10);
  --warn:#f59e0b;
  --warn-soft:rgba(245,158,11,.08);
  --err:#ef4444;
  --err-soft:rgba(239,68,68,.08);
  --text:#0d2d3d;
  --text-muted:#5a7a8a;
  --text-mutest:#8aa6b5;
  --b:rgba(0,115,153,.10);
  --b2:rgba(0,115,153,.20);
  --r:10px;
  --rL:14px;
  --tr:.18s ease;
  --ff-display:'Bebas Neue',sans-serif;
  --ff-body:'DM Sans',sans-serif;
  --ff-mono:'Space Mono',monospace;

  font-family:var(--ff-body);
  color:var(--text);
  background:var(--n1);
  width:100%;
  display:flex;
  flex-direction:column;
  border-radius:var(--rL);
  overflow:hidden;
  min-height:600px
}

/* ══════════════════════════════════════════════════════
   HEADER
══════════════════════════════════════════════════════ */
.gos-hdr{
  display:flex;
  align-items:center;
  justify-content:space-between;
  padding:14px 22px;
  border-bottom:1.5px solid var(--n4);
  background:var(--n2);
  flex-shrink:0;
  gap:12px;
  flex-wrap:wrap
}
.gos-brand{
  display:flex;
  align-items:center;
  gap:10px;
  flex-wrap:wrap;
  min-width:0;
  flex:1
}
.gos-logo{
  font-family:var(--ff-display);
  font-size:20px;
  letter-spacing:3px;
  background:linear-gradient(135deg,var(--primary),var(--primary-light));
  -webkit-background-clip:text;
  -webkit-text-fill-color:transparent;
  background-clip:text;
  flex-shrink:0
}
.gos-oid{
  font-family:var(--ff-mono);
  font-size:10px;
  color:var(--text-muted);
  background:var(--n1);
  border:1px solid var(--n4);
  border-radius:6px;
  padding:3px 9px;
  letter-spacing:.5px;
  max-width:220px;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap
}
.gos-badge{
  display:inline-flex;
  align-items:center;
  gap:5px;
  padding:4px 11px;
  border-radius:20px;
  font-size:10px;
  font-weight:700;
  text-transform:uppercase;
  letter-spacing:.5px;
  border:1px solid;
  flex-shrink:0
}
.gos-badge.pending{background:var(--warn-soft);color:var(--warn);border-color:rgba(245,158,11,.25)}
.gos-badge.review,.gos-badge.in_review{background:var(--teal-soft);color:var(--teal);border-color:rgba(79,172,254,.25)}
.gos-badge.draft_ready{background:var(--gold-soft);color:var(--gold);border-color:rgba(201,162,39,.25)}
.gos-badge.approved{background:var(--emerald-soft);color:var(--emerald);border-color:rgba(52,199,89,.25)}
.gos-badge.rejected{background:var(--err-soft);color:var(--err);border-color:rgba(239,68,68,.25)}
.gos-badge.completed{background:var(--gold-soft);color:var(--gold);border-color:rgba(201,162,39,.25)}

/* ══════════════════════════════════════════════════════
   SOURCE BANNER
══════════════════════════════════════════════════════ */
.gos-source-banner{
  display:flex;
  align-items:center;
  gap:14px;
  padding:14px 22px;
  border-bottom:1.5px solid var(--n4);
  flex-shrink:0;
  flex-wrap:wrap
}
.gos-source-banner.custom{background:linear-gradient(135deg,var(--teal-soft),transparent)}
.gos-source-banner.template{background:linear-gradient(135deg,var(--emerald-soft),transparent)}
.gos-source-banner.ask{background:linear-gradient(135deg,var(--gold-soft),transparent)}
.gos-source-icon{
  font-size:22px;
  width:42px;
  height:42px;
  border-radius:50%;
  display:flex;
  align-items:center;
  justify-content:center;
  flex-shrink:0;
  border:1.5px solid var(--n4)
}
.gos-source-banner.custom .gos-source-icon{background:var(--n1);color:var(--teal);border-color:rgba(79,172,254,.3)}
.gos-source-banner.template .gos-source-icon{background:var(--n1);color:var(--emerald);border-color:rgba(52,199,89,.3)}
.gos-source-banner.ask .gos-source-icon{background:var(--n1);color:var(--gold);border-color:rgba(201,162,39,.3)}
.gos-source-info{flex:1;min-width:200px}
.gos-source-title{
  font-size:14px;
  font-weight:700;
  letter-spacing:.3px
}
.gos-source-banner.custom .gos-source-title{color:var(--teal)}
.gos-source-banner.template .gos-source-title{color:var(--emerald)}
.gos-source-banner.ask .gos-source-title{color:var(--gold)}
.gos-source-sub{
  font-size:11px;
  color:var(--text-muted);
  margin-top:3px;
  line-height:1.4
}
.gos-source-meta{
  font-size:10.5px;
  color:var(--text-mutest);
  margin-top:4px
}
.gos-source-meta strong{color:var(--text)}
.gos-source-actions{display:flex;gap:6px;flex-shrink:0;flex-wrap:wrap}

/* ══════════════════════════════════════════════════════
   TABS
══════════════════════════════════════════════════════ */
.gos-tabs{
  display:flex;
  border-bottom:1.5px solid var(--n4);
  background:var(--n2);
  flex-shrink:0;
  overflow-x:auto;
  scrollbar-width:none
}
.gos-tabs::-webkit-scrollbar{display:none}
.gos-tab{
  position:relative;
  padding:14px 22px;
  background:transparent;
  border:none;
  border-right:1px solid var(--n4);
  color:var(--text-muted);
  cursor:pointer;
  font-family:var(--ff-body);
  font-size:12px;
  font-weight:600;
  letter-spacing:.3px;
  display:flex;
  align-items:center;
  gap:8px;
  transition:all var(--tr);
  white-space:nowrap;
  flex:1;
  justify-content:center
}
.gos-tab:last-child{border-right:none}
.gos-tab::after{
  content:'';
  position:absolute;
  bottom:-1.5px;
  left:0;right:0;
  height:3px;
  background:linear-gradient(90deg,var(--primary),var(--primary-light));
  transform:scaleX(0);
  transition:transform var(--tr)
}
.gos-tab.on{color:var(--primary);background:var(--n1);font-weight:700}
.gos-tab.on::after{transform:scaleX(1)}
.gos-tab:hover:not(.on){color:var(--text);background:var(--n3)}
.gos-tab-badge{
  font-size:9px;
  font-weight:700;
  padding:2px 8px;
  border-radius:20px;
  text-transform:uppercase;
  letter-spacing:.4px;
  border:1px solid
}
.gos-tab-badge.gold{background:var(--gold-soft);color:var(--gold);border-color:rgba(201,162,39,.3)}
.gos-tab-badge.teal{background:var(--teal-soft);color:var(--teal);border-color:rgba(79,172,254,.3)}
.gos-tab-badge.warn{background:var(--warn-soft);color:var(--warn);border-color:rgba(245,158,11,.3)}
.gos-tab-badge.ok{background:var(--emerald-soft);color:var(--emerald);border-color:rgba(52,199,89,.3)}

/* ══════════════════════════════════════════════════════
   STATUS BAR + VERSIONS
══════════════════════════════════════════════════════ */
.gos-save-status{
  padding:10px 22px;
  border-bottom:1px solid var(--n4);
  display:flex;
  align-items:center;
  gap:10px;
  font-size:12px;
  background:var(--n2);
  flex-shrink:0;
  flex-wrap:wrap
}
.gos-save-status.busy{background:var(--primary-softer);color:var(--primary)}
.gos-save-status.saved{background:var(--emerald-soft);color:var(--emerald)}
.gos-save-status.error{background:var(--err-soft);color:var(--err)}
.gos-save-icon{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:24px;height:24px;
  border-radius:50%;
  font-size:13px;
  flex-shrink:0;
  background:rgba(255,255,255,.4)
}
.gos-save-status.busy .gos-save-icon{animation:gos-pulse 1.4s ease infinite}
@keyframes gos-pulse{0%,100%{opacity:1}50%{opacity:.5}}

.gos-version-strip{
  display:flex;
  gap:6px;
  padding:10px 22px;
  border-bottom:1px solid var(--n4);
  background:var(--n2);
  align-items:center;
  flex-shrink:0;
  flex-wrap:wrap
}
.gos-version-dot{
  width:10px;
  height:10px;
  border-radius:50%;
  flex-shrink:0;
  transition:all var(--tr)
}
.gos-version-dot.used{background:var(--primary)}
.gos-version-dot.current{background:var(--teal);box-shadow:0 0 0 3px rgba(79,172,254,.3)}
.gos-version-dot.empty{background:var(--n4);border:1.5px solid var(--n5)}
.gos-version-label{
  font-size:11px;
  color:var(--text-muted);
  margin-left:6px;
  font-weight:600
}
.gos-version-label strong{color:var(--primary)}

/* ══════════════════════════════════════════════════════
   BODY
══════════════════════════════════════════════════════ */
.gos-body{
  flex:1;
  background:var(--n0);
  overflow:hidden;
  display:flex;
  flex-direction:column;
  min-height:400px
}

/* ══════════════════════════════════════════════════════
   BUTTONS
══════════════════════════════════════════════════════ */
.gos-btn{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:6px;
  padding:9px 18px;
  border-radius:8px;
  font-family:var(--ff-body);
  font-size:12px;
  font-weight:600;
  cursor:pointer;
  border:none;
  transition:all var(--tr);
  letter-spacing:.4px;
  text-transform:uppercase;
  white-space:nowrap
}
.gos-btn:disabled{opacity:.45;cursor:not-allowed;pointer-events:none}
.gos-btn-primary{
  background:linear-gradient(135deg,var(--primary),var(--primary-light));
  color:#fff;
  box-shadow:0 4px 14px rgba(0,115,153,.28)
}
.gos-btn-primary:hover:not(:disabled){
  filter:brightness(1.08);
  transform:translateY(-1px);
  box-shadow:0 6px 20px rgba(0,115,153,.38)
}
.gos-btn-teal{
  background:linear-gradient(135deg,var(--teal),var(--teal-light));
  color:#fff;
  box-shadow:0 4px 14px rgba(79,172,254,.28)
}
.gos-btn-teal:hover:not(:disabled){
  filter:brightness(1.08);
  transform:translateY(-1px)
}
.gos-btn-success{
  background:linear-gradient(135deg,var(--emerald),#4ade80);
  color:#fff;
  box-shadow:0 4px 14px rgba(52,199,89,.28)
}
.gos-btn-success:hover:not(:disabled){filter:brightness(1.08);transform:translateY(-1px)}
.gos-btn-gold{
  background:linear-gradient(135deg,var(--gold),#f0d060);
  color:#fff;
  box-shadow:0 4px 14px rgba(201,162,39,.28)
}
.gos-btn-gold:hover:not(:disabled){filter:brightness(1.08);transform:translateY(-1px)}
.gos-btn-secondary{
  background:var(--n2);
  color:var(--text);
  border:1.5px solid var(--n4)
}
.gos-btn-secondary:hover:not(:disabled){
  background:var(--n3);
  border-color:var(--primary)
}
.gos-btn-ghost{
  background:transparent;
  color:var(--text-muted);
  border:1.5px solid var(--n4)
}
.gos-btn-ghost:hover:not(:disabled){
  color:var(--primary);
  border-color:var(--primary);
  background:var(--primary-softer)
}
.gos-btn-sm{padding:6px 12px;font-size:10.5px}
.gos-btn-close{
  width:36px;height:36px;
  padding:0;
  border-radius:50%;
  background:var(--n2);
  border:1.5px solid var(--n4);
  color:var(--text-muted);
  font-size:14px;
  cursor:pointer;
  display:flex;
  align-items:center;
  justify-content:center;
  transition:all var(--tr);
  flex-shrink:0
}
.gos-btn-close:hover{
  background:var(--err);
  border-color:var(--err);
  color:#fff;
  transform:rotate(90deg)
}

/* ══════════════════════════════════════════════════════
   FOOTER
══════════════════════════════════════════════════════ */
.gos-footer{
  display:flex;
  align-items:center;
  justify-content:space-between;
  padding:14px 22px;
  border-top:1.5px solid var(--n4);
  background:var(--n1);
  gap:14px;
  flex-wrap:wrap;
  flex-shrink:0
}
.gos-footer-l{
  display:flex;
  align-items:center;
  gap:14px;
  flex-wrap:wrap
}
.gos-footer-r{
  display:flex;
  align-items:center;
  gap:8px;
  flex-wrap:wrap
}
.gos-price-label{
  font-size:10px;
  color:var(--text-muted);
  text-transform:uppercase;
  letter-spacing:1px;
  font-weight:600;
  display:block;
  margin-bottom:2px
}
.gos-price-value{
  font-family:var(--ff-display);
  font-size:24px;
  font-weight:700;
  background:linear-gradient(135deg,var(--primary),var(--primary-light));
  -webkit-background-clip:text;
  -webkit-text-fill-color:transparent;
  background-clip:text;
  letter-spacing:1px;
  line-height:1
}

/* ══════════════════════════════════════════════════════
   DESIGN STUDIO WRAPPER
══════════════════════════════════════════════════════ */
.gos-studio-wrap{
  flex:1;
  display:flex;
  flex-direction:column;
  overflow:hidden;
  background:var(--n0);
  min-height:0
}

/* ══════════════════════════════════════════════════════
   SUBMITTED SCREEN
══════════════════════════════════════════════════════ */
.gos-submitted-screen{
  flex:1;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  padding:40px 30px;
  text-align:center;
  gap:18px
}
.gos-submitted-icon{
  font-size:72px;
  filter:drop-shadow(0 8px 24px rgba(52,199,89,.4));
  animation:gos-bounce .5s ease
}
@keyframes gos-bounce{
  0%{opacity:0;transform:scale(.5)}
  50%{transform:scale(1.1)}
  100%{opacity:1;transform:scale(1)}
}
.gos-submitted-title{
  font-family:var(--ff-display);
  font-size:36px;
  letter-spacing:2px;
  color:var(--emerald)
}
.gos-submitted-sub{
  font-size:13px;
  color:var(--text-muted);
  max-width:440px;
  line-height:1.6
}

/* ══════════════════════════════════════════════════════
   ASK VIEWER (interface lecture seule)
══════════════════════════════════════════════════════ */
.gos-ask-viewer{
  flex:1;
  overflow-y:auto;
  padding:22px;
  background:var(--n0);
  scrollbar-width:thin;
  scrollbar-color:var(--n5) transparent
}
.gos-ask-viewer::-webkit-scrollbar{width:6px}
.gos-ask-viewer::-webkit-scrollbar-thumb{background:var(--n5);border-radius:3px}

.gos-print-area{
  background:var(--n1);
  color:var(--text);
  border-radius:var(--rL);
  padding:32px;
  max-width:760px;
  margin:0 auto;
  font-family:var(--ff-body);
  line-height:1.5;
  border:1px solid var(--n4);
  box-shadow:0 4px 16px rgba(0,40,60,.04)
}
.gos-print-head{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  padding-bottom:18px;
  border-bottom:2px solid var(--primary);
  margin-bottom:22px;
  gap:20px;
  flex-wrap:wrap
}
.gos-print-brand{
  font-family:var(--ff-display);
  font-size:30px;
  letter-spacing:3px;
  background:linear-gradient(135deg,var(--primary),var(--primary-light));
  -webkit-background-clip:text;
  -webkit-text-fill-color:transparent;
  background-clip:text
}
.gos-print-doc-info{
  text-align:right;
  font-size:11px;
  color:var(--text-muted)
}
.gos-print-doc-info-line{margin-bottom:3px}
.gos-print-doc-info-line strong{color:var(--text)}

.gos-print-section{margin-bottom:22px}
.gos-print-section-title{
  font-size:13px;
  font-weight:700;
  color:var(--primary);
  text-transform:uppercase;
  letter-spacing:.8px;
  padding-bottom:8px;
  border-bottom:1px solid var(--n4);
  margin-bottom:14px;
  display:flex;
  align-items:center;
  gap:8px
}
.gos-print-section-title::before{
  content:'';
  width:4px;
  height:14px;
  background:linear-gradient(180deg,var(--primary),var(--primary-light));
  border-radius:2px
}

.gos-print-meta-grid{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:10px
}
.gos-print-meta-item{
  padding:11px 14px;
  background:var(--n2);
  border:1px solid var(--n4);
  border-radius:8px;
  display:flex;
  flex-direction:column;
  gap:3px
}
.gos-print-meta-k{
  font-size:9.5px;
  color:var(--text-muted);
  text-transform:uppercase;
  font-weight:700;
  letter-spacing:.6px
}
.gos-print-meta-v{
  font-size:13px;
  color:var(--text);
  font-weight:600;
  word-break:break-word
}

.gos-print-field{margin-bottom:14px}
.gos-print-field-label{
  font-size:10px;
  font-weight:700;
  color:var(--text-muted);
  text-transform:uppercase;
  letter-spacing:.8px;
  margin-bottom:6px
}
.gos-print-field-label.avoid{color:var(--err)}
.gos-print-field-label.required{color:var(--emerald)}
.gos-print-field-value{
  padding:11px 14px;
  background:var(--n2);
  border:1px solid var(--n4);
  border-radius:8px;
  font-size:13px;
  color:var(--text);
  line-height:1.55;
  min-height:40px;
  white-space:pre-wrap;
  word-break:break-word
}
.gos-print-field-value.avoid{
  background:var(--err-soft);
  border-color:rgba(239,68,68,.25)
}
.gos-print-field-value.required{
  background:var(--emerald-soft);
  border-color:rgba(52,199,89,.25)
}
.gos-print-field-value.empty{
  color:var(--text-mutest);
  font-style:italic;
  font-size:12px
}

.gos-print-row-2{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:14px
}

.gos-print-chips{
  display:flex;
  flex-wrap:wrap;
  gap:6px
}
.gos-print-chip{
  padding:5px 13px;
  background:var(--primary-soft);
  color:var(--primary);
  border:1.5px solid var(--b2);
  border-radius:18px;
  font-size:11px;
  font-weight:600
}
.gos-print-chip.style{
  background:var(--teal-soft);
  color:var(--teal);
  border-color:rgba(79,172,254,.25)
}

.gos-print-refs-header{
  display:flex;align-items:center;justify-content:space-between;
  margin-bottom:10px
}
.gos-print-refs-grid{
  display:grid;
  grid-template-columns:repeat(2,1fr);
  gap:12px
}
.gos-print-ref-card{
  position:relative;
  aspect-ratio:4/3;
  border-radius:10px;
  overflow:hidden;
  background:var(--n2);
  border:1.5px solid var(--n4)
}
.gos-print-ref-card img{
  width:100%;
  height:100%;
  object-fit:cover;
  display:block
}
.gos-print-ref-num{
  position:absolute;
  top:6px;
  left:6px;
  background:rgba(0,40,60,.7);
  color:#fff;
  padding:3px 9px;
  border-radius:8px;
  font-size:10px;
  font-weight:700;
  backdrop-filter:blur(4px)
}
.gos-print-ref-dl{
  position:absolute;
  bottom:6px;right:6px;
  background:rgba(0,40,60,.75);color:#fff;
  border:none;border-radius:8px;
  width:28px;height:28px;
  display:flex;align-items:center;justify-content:center;
  font-size:13px;cursor:pointer;
  opacity:0;transition:opacity .18s;
  backdrop-filter:blur(3px)
}
.gos-print-ref-card:hover .gos-print-ref-dl{opacity:1}
.gos-dl-all-btn{
  display:inline-flex;align-items:center;gap:5px;
  background:var(--primary-soft);color:var(--primary);
  border:1px solid var(--b2);border-radius:8px;
  padding:5px 12px;font-size:11px;font-weight:700;
  cursor:pointer;transition:all .18s
}
.gos-dl-all-btn:hover{background:var(--primary);color:#fff}

.gos-print-footer{
  margin-top:30px;
  padding-top:18px;
  border-top:1px solid var(--n4);
  font-size:10.5px;
  color:var(--text-mutest);
  text-align:center;
  line-height:1.6
}

/* ══════════════════════════════════════════════════════
   EMPTY STATE
══════════════════════════════════════════════════════ */
.gos-empty-state{
  flex:1;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap:18px;
  padding:60px 30px;
  text-align:center
}
.gos-empty-icon{
  font-size:72px;
  opacity:.18
}
.gos-empty-title{
  font-family:var(--ff-display);
  font-size:30px;
  letter-spacing:2px;
  color:var(--primary)
}
.gos-empty-sub{
  font-size:13px;
  color:var(--text-muted);
  max-width:380px;
  line-height:1.6
}

/* ══════════════════════════════════════════════════════
   ASK MULTI-REQUEST TABS
══════════════════════════════════════════════════════ */
.gos-ask-req-tabs{
  display:flex;gap:6px;flex-wrap:wrap;
  margin-bottom:18px
}
.gos-ask-req-tab{
  display:inline-flex;align-items:center;gap:6px;
  padding:7px 14px;border-radius:20px;
  border:1.5px solid var(--n4);background:var(--n2);
  color:var(--text-muted);font-size:11px;font-weight:600;
  cursor:pointer;transition:all .18s ease
}
.gos-ask-req-tab:hover:not(.on){border-color:var(--primary);color:var(--text)}
.gos-ask-req-tab.on{
  border-color:var(--gold);background:var(--gold-soft);color:var(--gold)
}
.gos-ask-req-tab.on.revision{
  border-color:var(--warn);background:var(--warn-soft);color:var(--warn)
}
.gos-ask-req-num{
  display:inline-flex;align-items:center;justify-content:center;
  width:20px;height:20px;border-radius:50%;
  background:var(--gold);color:#fff;
  font-size:10px;font-weight:700;flex-shrink:0
}
.gos-ask-req-num.revision{background:var(--warn)}
.gos-ask-req-meta{
  display:flex;gap:8px;flex-wrap:wrap;
  margin-bottom:14px;padding:10px 14px;
  background:var(--n2);border:1px solid var(--n4);
  border-radius:8px
}
.gos-ask-req-meta-item{
  font-size:11px;color:var(--text-muted)
}
.gos-ask-req-meta-item strong{color:var(--text)}
.gos-ask-empty{
  padding:40px 20px;text-align:center;color:var(--text-muted)
}
.gos-ask-empty-icon{font-size:48px;opacity:.2;margin-bottom:10px}
.gos-ask-empty-title{font-size:15px;font-weight:700;color:var(--text);margin-bottom:6px}
.gos-ask-empty-sub{font-size:12px}

/* ══════════════════════════════════════════════════════
   PRINT MEDIA
══════════════════════════════════════════════════════ */
@media print{
  body * {visibility:hidden!important}
  .gos-print-area, .gos-print-area * {visibility:visible!important}
  .gos-print-area {position:absolute;left:0;top:0;width:100%;padding:20px;max-width:none;border:none;border-radius:0;box-shadow:none}
}

/* ══════════════════════════════════════════════════════
   RESPONSIVE
══════════════════════════════════════════════════════ */
@media(max-width:900px){
  .gos-tabs{grid-template-columns:1fr}
  .gos-tab{
    padding:12px 16px
  }
  .gos-footer{flex-direction:column;align-items:stretch}
  .gos-footer-l,.gos-footer-r{justify-content:center}
  .gos-print-area{padding:24px}
}
@media(max-width:600px){
  .gos-hdr,.gos-source-banner,.gos-save-status,.gos-version-strip,.gos-footer{padding:12px 14px}
  .gos-body,.gos-ask-viewer{padding:14px}
  .gos-print-meta-grid,.gos-print-row-2,.gos-print-refs-grid{grid-template-columns:1fr}
  .gos-print-head{flex-direction:column;align-items:stretch}
  .gos-print-doc-info{text-align:left}
  .gos-print-area{padding:18px}
  .gos-price-value{font-size:20px}
  .gos-submitted-title{font-size:28px}
  .gos-empty-title{font-size:24px}
}
`;

/* ══════════════════════════════════════════════════════
   HELPERS
══════════════════════════════════════════════════════ */

const extractProduct = (order) => {
  if (!order) return null;
  const p = order.products;
  if (!p) return null;
  if (Array.isArray(p)) return p[0] || null;
  if (typeof p === "object") return p;
  return null;
};

const formatDate = (date) => {
  if (!date) return null;
  try {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric", month: "long", day: "numeric"
    });
  } catch { return String(date); }
};

const formatSubmitted = (date) => {
  if (!date) return "—";
  try {
    return new Date(date).toLocaleString("en-US", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  } catch { return String(date); }
};

const formatSourceTypeLabel = (sourceType) => {
  switch (sourceType) {
    case "custom":   return { icon: "🎨", label: __elakayText("t029", "Custom Design"), sub: elakayAutoText("graffiti_seller_source_custom_help", "Client submitted their own design") };
    case "ask":      return { icon: "💬", label: __elakayText("t030", "Custom Request"), sub: elakayAutoText("graffiti_seller_source_ask_help", "Client requested a custom design — needs your work") };
    case "template": return { icon: "📋", label: __elakayText("t031", "Template Selected"), sub: elakayAutoText("graffiti_seller_source_template_help", "Client chose one of your existing templates") };
    default:         return { icon: "❓", label: __elakayText("t049", "Unknown"), sub: "" };
  }
};

const formatStatusLabel = (status) => {
  switch (status) {
    case "pending":     return elakayAutoText("graffiti_status_pending", "⏳ Pending");
    case "in_review":
    case "review":      return elakayAutoText("graffiti_status_in_review", "🔍 In Review");
    case "draft_ready": return elakayAutoText("graffiti_status_draft_ready", "📝 Draft Ready");
    case "approved":    return elakayAutoText("graffiti_status_approved", "✓ Approved");
    case "rejected":    return elakayAutoText("graffiti_status_rejected", "✕ Rejected");
    case "completed":   return elakayAutoText("graffiti_status_completed", "✅ Completed");
    default:            return status || "—";
  }
};

const MAX_REVISIONS = 3;

/* Resolve a ref image to a displayable URL (File, string URL, or saved object) */
const resolveRefSrc = (ref) => {
  if (!ref) return null;
  if (typeof File !== "undefined" && ref instanceof File) return URL.createObjectURL(ref);
  if (typeof ref === "string" && ref.length > 0) return ref;
  if (typeof ref === "object" && ref.urlImg) return ref.urlImg;
  return null;
};

const downloadRefImage = async (src, filename = "reference.png") => {
  try {
    const dlUrl = src.includes("ask-ref-image")
      ? src + (src.includes("?") ? "&download=1" : "?download=1")
      : src;
    const resp = await fetch(dlUrl);
    const blob = await resp.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(blobUrl); }, 300);
  } catch (err) {
    console.error("[downloadRefImage] Failed:", err);
  }
};

const downloadAllRefImages = async (images, askIndex = 0) => {
  const items = (images || []).map(r => resolveRefSrc(r)).filter(Boolean);
  for (let i = 0; i < items.length; i++) {
    await downloadRefImage(items[i], `ask${askIndex + 1}_ref${i + 1}.png`);
    if (i < items.length - 1) await new Promise(res => setTimeout(res, 400));
  }
};

/* ══════════════════════════════════════════════════════
   ASK ORDER VIEWER
══════════════════════════════════════════════════════ */
function AskOrderViewer({ order, graphic, product, infoSource }) {
  __setElakayInfoSource(infoSource);
  const text = (key, fallback) => getHardcodedText(infoSource, "graffitiOrderSeller", key, fallback);

  const orderId = order?.orders_id || order?._id || "—";

  const askRequests = useMemo(() => {
    const data = graphic?.askSellerData;
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return [data];
  }, [graphic?.askSellerData]);

  const [activeReqIndex, setActiveReqIndex] = useState(0);

  const safeIndex = Math.min(activeReqIndex, Math.max(0, askRequests.length - 1));
  const ask = askRequests[safeIndex] || null;

  const renderField = (value, extraClass = "") => {
    const empty = !value || (typeof value === "string" && !value.trim());
    if (empty) return <div className={`gos-print-field-value empty ${extraClass}`}>{text("t001", "— Not specified")}</div>;
    return <div className={`gos-print-field-value ${extraClass}`}>{value}</div>;
  };

  const isAskEmpty = useMemo(() => {
    if (!ask) return true;
    return !ask.inspirations?.length &&
           !ask.stylePrefs?.length &&
           !ask.budget &&
           !ask.detailLevel &&
           !ask.specialInstructions?.trim() &&
           !ask.artistMessage?.trim() &&
           !ask.avoidInstructions?.trim() &&
           !ask.requiredInstructions?.trim() &&
           !ask.images?.some(r => resolveRefSrc(r)) &&
           !ask.desiredDate;
  }, [ask]);

  if (askRequests.length === 0) {
    return (
      <div className="gos-ask-viewer">
        <div className="gos-print-area">
          <div className="gos-print-head">
            <div>
              <div className="gos-print-brand">ELAKAY</div>
              <div style={{ fontSize: 11, color: "#5a7a8a", marginTop: 4 }}>{__elakayText("t032", "Custom Design Request")}</div>
            </div>
            <div className="gos-print-doc-info">
              <div className="gos-print-doc-info-line"><strong>{text("t002", "Order #")}</strong> {orderId}</div>
              <div className="gos-print-doc-info-line"><strong>{text("t004", "Status:")}</strong> {formatStatusLabel(graphic?.graphicStatus)}</div>
            </div>
          </div>
          <div className="gos-ask-empty">
            <div className="gos-ask-empty-icon">📝</div>
            <div className="gos-ask-empty-title">{__elakayText("t033", "No Request Yet")}</div>
            <div className="gos-ask-empty-sub">{__elakayText("t034", "The client has not sent any request yet.")}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gos-ask-viewer">
      <div className="gos-print-area">

        {/* Header */}
        <div className="gos-print-head">
          <div>
            <div className="gos-print-brand">ELAKAY</div>
            <div style={{ fontSize: 11, color: "#5a7a8a", marginTop: 4 }}>{__elakayText("t032", "Custom Design Request")}</div>
          </div>
          <div className="gos-print-doc-info">
            <div className="gos-print-doc-info-line"><strong>{text("t002", "Order #")}</strong> {orderId}</div>
            <div className="gos-print-doc-info-line"><strong>{text("t003", "Submitted:")}</strong> {formatSubmitted(graphic?.submittedAt)}</div>
            <div className="gos-print-doc-info-line"><strong>{text("t004", "Status:")}</strong> {formatStatusLabel(graphic?.graphicStatus)}</div>
          </div>
        </div>

        {/* Request selector tabs */}
        {askRequests.length > 1 && (
          <div className="gos-ask-req-tabs">
            {askRequests.map((req, i) => {
              const isRevision = i > 0;
              return (
                <button key={i} type="button"
                  className={`gos-ask-req-tab${safeIndex === i ? " on" : ""}${isRevision ? " revision" : ""}`}
                  onClick={() => setActiveReqIndex(i)}>
                  <span className={`gos-ask-req-num${isRevision ? " revision" : ""}`}>{i + 1}</span>
                  {isRevision ? `Revision #${i + 1}` : elakayAutoText("h_5192d6a1a3eee3", "Original Request")}
                </button>
              );
            })}
          </div>
        )}

        {/* Request meta info */}
        {ask && (ask.submittedAt || ask.desiredDate || ask.budget || ask.detailLevel) && (
          <div className="gos-ask-req-meta">
            {ask.submittedAt && (
              <span className="gos-ask-req-meta-item">
                📅 <strong>{text("t003", "Sent:")}</strong> {formatSubmitted(ask.submittedAt)}
              </span>
            )}
            {ask.desiredDate && (
              <span className="gos-ask-req-meta-item">
                🗓️ <strong>{text("t009", "Desired:")}</strong> {formatDate(ask.desiredDate)}
              </span>
            )}
            {ask.budget && (
              <span className="gos-ask-req-meta-item">
                💰 <strong>{text("t013", "Budget:")}</strong> {ask.budget}
              </span>
            )}
            {ask.detailLevel && (
              <span className="gos-ask-req-meta-item">
                🔍 <strong>{text("t014", "Detail:")}</strong> {ask.detailLevel}
              </span>
            )}
          </div>
        )}

        {/* Product info */}
        {(product || order?.prod_name) && (
          <div className="gos-print-section">
            <div className="gos-print-section-title">📦 {text("t005", "Product")}</div>
            <div className="gos-print-meta-grid">
              <div className="gos-print-meta-item">
                <span className="gos-print-meta-k">{text("t006", "Name")}</span>
                <span className="gos-print-meta-v">{order?.prod_name || product?.name || "—"}</span>
              </div>
              <div className="gos-print-meta-item">
                <span className="gos-print-meta-k">{text("t007", "Variant / Qty")}</span>
                <span className="gos-print-meta-v">
                  {product?.variant || "—"}{product?.qty && ` × ${product.qty}`}
                </span>
              </div>
              <div className="gos-print-meta-item">
                <span className="gos-print-meta-k">{text("t008", "Price")}</span>
                <span className="gos-print-meta-v">
                  ${product?.price?.toLocaleString?.("en-US") || product?.price || "—"}
                </span>
              </div>
            </div>
          </div>
        )}

        {isAskEmpty && (
          <div className="gos-ask-empty">
            <div className="gos-ask-empty-icon">📝</div>
            <div className="gos-ask-empty-title">{__elakayText("t033", "Empty Request")}</div>
            <div className="gos-ask-empty-sub">{__elakayText("t034", "The client did not provide specific instructions.")}</div>
          </div>
        )}

        {/* Style Inspirations */}
        {ask?.inspirations?.length > 0 && (
          <div className="gos-print-section">
            <div className="gos-print-section-title">💡 {text("t010", "Style Inspirations")}</div>
            <div className="gos-print-chips">
              {ask.inspirations.map((insp, i) => (
                <span key={i} className="gos-print-chip">{insp}</span>
              ))}
            </div>
          </div>
        )}

        {/* Artistic styles */}
        {ask?.stylePrefs?.length > 0 && (
          <div className="gos-print-section">
            <div className="gos-print-section-title">🎨 {text("t011", "Artistic Styles")}</div>
            <div className="gos-print-chips">
              {ask.stylePrefs.map((s, i) => (
                <span key={i} className="gos-print-chip style">{s}</span>
              ))}
            </div>
          </div>
        )}

        {/* Instructions */}
        {ask && (ask.specialInstructions || ask.artistMessage || ask.avoidInstructions || ask.requiredInstructions) && (
          <div className="gos-print-section">
            <div className="gos-print-section-title">📝 {text("t015", "Instructions")}</div>

            {ask.specialInstructions && (
              <div className="gos-print-field">
                <div className="gos-print-field-label">{text("t016", "General Instructions")}</div>
                {renderField(ask.specialInstructions)}
              </div>
            )}

            {ask.artistMessage && (
              <div className="gos-print-field">
                <div className="gos-print-field-label">{text("t017", "Message to the Artist")}</div>
                {renderField(ask.artistMessage)}
              </div>
            )}

            {(ask.avoidInstructions || ask.requiredInstructions) && (
              <div className="gos-print-row-2">
                {ask.avoidInstructions && (
                  <div className="gos-print-field">
                    <div className="gos-print-field-label avoid">❌ {text("t018", "Avoid")}</div>
                    {renderField(ask.avoidInstructions, "avoid")}
                  </div>
                )}
                {ask.requiredInstructions && (
                  <div className="gos-print-field">
                    <div className="gos-print-field-label required">✅ {text("t019", "Required")}</div>
                    {renderField(ask.requiredInstructions, "required")}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Reference Images */}
        {ask?.images?.some(r => resolveRefSrc(r)) && (
          <div className="gos-print-section">
            <div className="gos-print-refs-header">
              <div className="gos-print-section-title" style={{ margin: 0 }}>
                🖼️ {text("t020", "Reference Images")}
              </div>
              {ask.images.filter(r => resolveRefSrc(r)).length > 1 && (
                <button type="button" className="gos-dl-all-btn"
                  onClick={() => downloadAllRefImages(ask.images, safeIndex)}>
                  ⬇ {__elakayText("t099", "Download All")}
                </button>
              )}
            </div>
            <div className="gos-print-refs-grid">
              {ask.images.map((ref, i) => {
                const src = resolveRefSrc(ref);
                if (!src) return null;
                return (
                  <div key={i} className="gos-print-ref-card">
                    <img src={src} alt={elakayAutoText("h_5e13f5795099fc", "Reference {value0}", { value0: i + 1 })}
                      crossOrigin="anonymous"
                      onError={e => { e.target.style.display = "none"; }} />
                    <span className="gos-print-ref-num">{__elakayText("t050", "Ref")}{i + 1}</span>
                    <button type="button" className="gos-print-ref-dl"
                      title={elakayAutoText("h_83d2a36b956934", "Download ref {value0}", { value0: i + 1 })}
                      onClick={() => downloadRefImage(src, `ask${safeIndex + 1}_ref${i + 1}.png`)}>
                      ⬇
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Seller Notes */}
        {graphic?.sellerNotes && (
          <div className="gos-print-section">
            <div className="gos-print-section-title">💼 {text("t021", "Seller Notes")}</div>
            {renderField(graphic.sellerNotes)}
          </div>
        )}

        <div className="gos-print-footer">
          {__elakayText("t035", "Generated by Elakay ·")}{formatSubmitted(new Date())}
          <br />{__elakayText("t036", "Custom design request submitted by the client.")}
        </div>

      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════ */
export default function GraffitiOrderSeller({
  order,
  infoSource,
  ImagesProd,
  onClose = () => {},
  onSaved = () => {}
  
}) {
  const text = (key, fallback) => getHardcodedText(infoSource, "graffitiOrderSeller", key, fallback);

  const { token } = isAuthenticated();

  const product = useMemo(() => extractProduct(order), [order]);
  const graphic = product?.graphic;
  const sourceType = graphic?.sourceType;
  const orderId = order?.orders_id || order?._id;

    /* 🆕 Hook de sauvegarde — mode SELLER */
  const save = useGraphicOrderSave({
    productID: product?.productID,
    authToken: token,
    pubToken: "",
    orderId: orderId,                       /* 🆕 ID de l'order existant */
    mode: "seller",                         /* 🆕 mode seller */
    infoSource,
    onCheckoutSuccess: (data) => {
      setSubmitted(true);
      onSaved(data);
    },
    onCheckoutError: (err) => {
      console.error("[GraffitiOrderSeller] Submit error:", err);
    },
    enableAutoSave: true
  });

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [activeTab, setActiveTab] = useState("request");

  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeSlotId, setActiveSlotId] = useState(null);
  const [images, setImages] = useState("");

  const [studioSlots, setStudioSlots] = useState([]);
  const [studioMeta, setStudioMeta] = useState({
    label: "",
    style: "Street Art",
    detail: "Moderate"
  });
  const [studioDesigns, setStudioDesigns] = useState([]);
  const [studioNotes, setStudioNotes] = useState("");

  const [saveError, setSaveError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

/* ─── Init du studio depuis les slots existants (seller-made uniquement) ─── */
  useEffect(() => {
    if (sourceType !== "ask") return;

    /* 🆕 Récupérer les slots déjà créés par le seller (s'il y en a) */
    const sellerSlots = (graphic?.slots || []).filter(s => s.graphicMade === "seller");

    if (sellerSlots.length > 0) {
      /* Restauration depuis le serveur */
      setStudioSlots(sellerSlots.map((s, i) => ({
        id: s.id || `seller_slot_${i}_${Date.now()}`,
        label: s.label || `Face ${i + 1}`,
        url: s.url || "",
        localUrl: ""
      })));

      setStudioDesigns(sellerSlots.map(s => ({
        texts: s.slotState?.texts || [],
        layers: s.slotState?.layers || [],
        instructions: s.slotState?.instructions || "",
        zoom: 1,
        pan: { x: 0, y: 0 }
      })));

      /* Si graphicStatus === "draft_ready" → déjà submitted */
      if (graphic.graphicStatus === "draft_ready" || 
          graphic.graphicStatus === "approved" ||
          graphic.graphicStatus === "completed") {
        setSubmitted(true);
      }
    } else {
      /* Init avec un slot vide utilisant l'image du produit */
      setStudioSlots([{
        id: `seller_slot_${Date.now()}`,
        label: __elakayText("t051", "Front"),
        url: product?.urlImg || "",
        localUrl: ""
      }]);
      setStudioDesigns([makeSlotDesign()]);
    }
  }, [sourceType, graphic, product]);


  /* ─── buildPayload — uniquement les données DesignStudio du seller ─── */
  const buildPayload = useCallback(() => {
    const slotsForBackend = studioSlots.map((slot, i) => {
      const design = studioDesigns[i] || makeSlotDesign();
      return {
        id:    slot.id,
        label: slot.label,
        url:   slot.localUrl || slot.url || "",
        graphicMade: "seller",
        slotState: {
          texts: design.texts?.map(t => ({ ...t })) || [],
          layers: design.layers?.map(l => ({
            originalLayerId: l._id || null,
            file:     l.file,          /* File objects extraits par le hook (extractFilesFromPayload) */
            urlImg:   l.src || l.url || l.urlImg || "",
            mimeType: l.mimeType || "image/png",
            size:     l.size,
            rotation: l.rotation,
            opacity:  l.opacity,
            pos:      { ...l.pos }
          })) || [],
          instructions: design.instructions || ""
        }
      };
    });

    return {
      sourceType:  sourceType,
      slots:       slotsForBackend,
      sellerNotes: studioNotes
      /*
       * askSellerData, templateMeta, templateRef sont intentionnellement
       * exclus : le seller ne soumet que son travail (DesignStudio).
       * Le backend (updateOrderGraphic, branche "seller") préserve
       * automatiquement askSellerData depuis la base de données.
       */
    };
  }, [studioSlots, studioDesigns, studioNotes, sourceType]);

   /* ─── canSave : au moins 1 slot avec contenu ─── */
  const canSave = useMemo(() => {
    if (studioSlots.length === 0) return false;
    const hasContent = studioDesigns.some(d =>
      (d?.texts?.length > 0 && d.texts.some(t => t.content?.trim())) ||
      (d?.layers?.length > 0)
    );
    return hasContent;
  }, [studioSlots, studioDesigns]);

  // const handleSaveDraft = useCallback(async () => {
  //   if (!orderId) {
  //     setSaveError("Order ID missing");
  //     return;
  //   }

  //   if (studioSlots.length === 0) {
  //     setSaveError("Add at least one face before saving");
  //     return;
  //   }

  //   setSaveBusy(true);
  //   setSaveMode("draft");
  //   setSaveError(null);

  //   try {
  //     const payload = buildSellerPayload();
  //     const result = await saveSellerDesign(token, orderId, payload);

  //     if (result?.success === false) {
  //       throw new Error(result.error || "Save failed");
  //     }

  //     setLastSavedAt(Date.now());
  //     onSaved(result.sellerDesign);

  //   } catch (err) {
  //     console.error("[handleSaveDraft] Error:", err);
  //     setSaveError(err.message || "Failed to save draft");
  //   } finally {
  //     setSaveBusy(false);
  //     setSaveMode(null);
  //   }
  // }, [orderId, studioSlots, buildSellerPayload, token, onSaved]);

    /* ─── Handler Save Draft ─── */
  const handleSaveDraft = useCallback(async () => {
    const payload = buildPayload();
    if (!payload) return;
    /* En mode seller, saveDraftServer appelle le backend */
    await save.saveDraftServer(payload);
  }, [buildPayload, save]);

    /* ─── Handler Submit (final) ─── */
  const handleSubmit = useCallback(async () => {
    if (!canSave) {
      alert(__elakayText("t052", "Add at least one text or image before submitting"));
      return;
    }

    const confirmed = window.confirm(
      __elakayText("t053", "Submit this design to the client? They will be notified.")
    );
    if (!confirmed) return;

    const payload = buildPayload();
    if (!payload) return;

    /* checkout en mode seller fait l'update backend + notify */
    await save.checkout(payload);
  }, [canSave, buildPayload, save]);

  const handleSubmitDesign = useCallback(async () => {
    if (!orderId) {
      setSaveError("Order ID missing");
      return;
    }

    if (studioSlots.length === 0) {
      setSaveError("Add at least one face before submitting");
      return;
    }

    const hasContent = studioDesigns.some(d =>
      (d?.texts?.length > 0 && d.texts.some(t => t.content?.trim())) ||
      (d?.layers?.length > 0)
    );

    if (!hasContent) {
      setSaveError("Add at least one text or image before submitting");
      return;
    }

    const confirmed = window.confirm(
      __elakayText("t053", "Submit this design to the client? They will be notified.")
    );
    if (!confirmed) return;

    setSaveError(null);

    /* updateOrderGraphic (seller branch) via the hook:
       sends only DesignStudio slots + sellerNotes,
       status "draft_ready" signals the client the design is ready */
    const payload = buildPayload();
    if (!payload) return;

    await save.checkout(payload);
  }, [orderId, studioSlots, studioDesigns, buildPayload, save]);

  // useEffect(() => {
  //   if (activeTab !== "design") return;
  //   if (submitted) return;
  //   if (studioSlots.length === 0) return;

  //   const timer = setTimeout(() => {
  //     const hasContent = studioDesigns.some(d =>
  //       (d?.texts?.length > 0) || (d?.layers?.length > 0)
  //     );
  //     if (hasContent && !saveBusy) {
  //       handleSaveDraft();
  //     }
  //   }, 30000);

  //   return () => clearTimeout(timer);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [studioSlots, studioDesigns, studioMeta, studioNotes, activeTab, submitted]);

    /* ─── Auto-save toutes les 30 secondes ─── */
  useEffect(() => {
    if (activeTab !== "design") return;
    if (submitted) return;
    if (!canSave) return;

    const timer = setTimeout(() => {
      if (!save.busy) {
        const payload = buildPayload();
        if (payload) {
          save.saveDraftServer(payload);
        }
      }
    }, 30000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studioSlots, studioDesigns, studioMeta, studioNotes, activeTab, submitted, canSave]);


  /* ─── Reconstruction pour DesignPreviewModal (custom/template) ─── */
  const reconstructedDesign = useMemo(() => {
    if (!graphic) return null;
    if (sourceType !== "custom" && sourceType !== "template") return null;

    const designId = sourceType === "template"
      ? graphic.templateRef
      : `local_custom_${graphic.submittedAt || Date.now()}`;

    return {
      _id: designId,
      label: graphic.templateMeta?.label || "Custom Design",
      style: graphic.templateMeta?.style || "—",
      detail: graphic.templateMeta?.detail || "—",
      status: "published",
      slots: (graphic.slots || []).map((s, idx) => ({
        _id: s.originalSlotId || s.id || `slot_${idx}`,
        id: s.id || `slot_${idx}`,
        label: s.label || `Face ${idx + 1}`,
        url: s.url || "",
        slotState: {
          texts: s.slotState?.texts || [],
          layers: (s.slotState?.layers || []).map(l => ({
            _id: l.originalLayerId || null,
            urlImg: l.urlImg || "",
            mimeType: l.mimeType || "image/png",
            size: l.size,
            rotation: l.rotation,
            opacity: l.opacity,
            pos: l.pos || { x: 50, y: 50 }
          })),
          instructions: s.slotState?.instructions || ""
        }
      })),
      isLocal: sourceType === "custom",
      isTemplate: sourceType === "template",
      sourceType
    };
  }, [graphic, sourceType]);

  /* ── Composant ImageGallery (utilisé dans ImagePickerModal) ── */
  const ImageGallery = () => (
    <div className="image-gallery-section">
      <div className="section-header">
        <h4 className="section-title">{__elakayText("t001", "All Image Items")}</h4>
      </div>
      <div className="image-grid">
        {ImagesProd?.galeries?.map((nm, i) => (
          <div key={i}
            className={`image-grid-item ${images === nm.urlImg ? 'selected' : ''}`}
            onClick={() => setImages(nm.urlImg)}>
            <ShowimagesLoading src={nm.urlImg} alt={nm._id} />
          </div>
        ))}
      </div>
    </div>
  );

   const sourceInfo = formatSourceTypeLabel(sourceType);
  const total = useMemo(() => {
    if (order?.amountTotal) return Number(order.amountTotal).toFixed(2);
    return ((product?.price || 0) * (product?.qty || 1)).toFixed(2);
  }, [order, product]);

  /* Pas de graphic → standard order */
  if (!graphic) {
    return (
      <>
        <style dangerouslySetInnerHTML={{ __html: CSS }} />
        <div className="gos-root">
          <div className="gos-hdr">
            <div className="gos-brand">
              <span className="gos-logo">ELAKAY</span>
              <span className="gos-oid">{orderId}</span>
            </div>
            <button type="button" className="gos-btn-close" onClick={onClose}>✕</button>
          </div>
          <div className="gos-empty-state">
            <div className="gos-empty-icon">📦</div>
            <div className="gos-empty-title">{text("t022", "Standard Order")}</div>
            <div className="gos-empty-sub">{__elakayText("t037", "This order does not contain any custom graphic customization.")}</div>
          </div>
          <div className="gos-footer">
            <div className="gos-footer-l"/>
            <div className="gos-footer-r">
              <button type="button" className="gos-btn gos-btn-ghost" onClick={onClose}>{text("t023", "Close")}</button>
            </div>
          </div>
        </div>
      </>
    );
  }

  /* ═══════════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════════ */
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: CSS + (CSS_ || "") }} />

      <div className="gos-root">

        {/* Header */}
        <div className="gos-hdr">
          <div className="gos-brand">
            <span className="gos-logo">ELAKAY</span>
            <span className="gos-oid">{orderId}</span>
            <span className={`gos-badge ${graphic.graphicStatus || "pending"}`}>
              {formatStatusLabel(graphic.graphicStatus)}
            </span>
          </div>
          <button type="button" className="gos-btn-close" onClick={onClose}>✕</button>
        </div>

        {/* Source Banner */}
        <div className={`gos-source-banner ${sourceType || "unknown"}`}>
          <div className="gos-source-icon">{sourceInfo.icon}</div>
          <div className="gos-source-info">
            <div className="gos-source-title">{sourceInfo.label}</div>
            <div className="gos-source-sub">{sourceInfo.sub}</div>
            {graphic.templateMeta?.label && (
              <div className="gos-source-meta">{__elakayText("t038", "Template:")}<strong>{graphic.templateMeta.label}</strong>
              </div>
            )}
          </div>
          <div className="gos-source-actions">
            {(sourceType === "custom" || sourceType === "template") && (
              <button type="button"
                className="gos-btn gos-btn-teal gos-btn-sm"
                onClick={() => setShowPreviewModal(true)}>{__elakayText("t039", "👁️ View Design")}</button>
            )}
          </div>
        </div>

        {/* TABS (mode ask uniquement) */}
        {sourceType === "ask" && (
          <div className="gos-tabs">
            <button type="button"
              className={`gos-tab${activeTab === "request" ? " on" : ""}`}
              onClick={() => setActiveTab("request")}>{__elakayText("t040", "💬 Client Request")}<span className="gos-tab-badge gold">{text("t024", "View")}</span>
            </button>
            <button type="button"
              className={`gos-tab${activeTab === "design" ? " on" : ""}`}
              onClick={() => setActiveTab("design")}>{__elakayText("t041", "🎨 Submit Design")}{submitted ? (
                <span className="gos-tab-badge ok">✓ {text("t025", "Submitted")}</span>
              ) : (graphic?.slots || []).filter(s => s.graphicMade === "seller").length > 0 ? (
                <span className="gos-tab-badge teal">{text("t026", "Draft")}</span>
              ) : (
                <span className="gos-tab-badge warn">{__elakayText("t054", "New")}</span>
              )}
            </button>
          </div>
        )}

        {/* Save status — utilise les states du hook */}
        {sourceType === "ask" && activeTab === "design" && 
         (save.busy || save.lastSavedAt || save.error) && (
          <div className={`gos-save-status ${
            save.busy ? "busy" :
            save.error ? "error" :
            "saved"
          }`}>
            <span className="gos-save-icon">
              {save.busy ? "⏳" : save.error ? "⚠️" : "✓"}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              {save.busy && save.busyMode === "draft" && elakayAutoText("h_fde1ab9eef594c", "Saving draft...")}
              {save.busy && save.busyMode === "checkout" && elakayAutoText("h_3aecb685663286", "Submitting to client...")}
              {!save.busy && save.error && `Error: ${save.error}`}
              {!save.busy && !save.error && save.lastSavedAt && (
                `Last saved: ${new Date(save.lastSavedAt).toLocaleTimeString()}`
              )}
            </span>
            {save.error && (
              <button type="button"
                className="gos-btn gos-btn-ghost gos-btn-sm"
                onClick={save.clearError}>
                ✕
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className="gos-body">

          {(sourceType === "custom" || sourceType === "template") && (
            <div className="gos-empty-state">
              <div className="gos-empty-icon">
                {sourceType === "custom" ? "🎨" : "📋"}
              </div>
              <div className="gos-empty-title">
                {sourceType === "custom" ? elakayAutoText("h_c8a5bc0ad2da09", "Custom Design Submitted") : elakayAutoText("h_0081f6e38542ec", "Template Customization")}
              </div>
              <div className="gos-empty-sub">
                {sourceType === "custom"
                  ? `The client created their own design with ${graphic.slots?.length || 0} face(s).`
                  : `The client selected template "${graphic.templateMeta?.label || ''}" with ${graphic.slots?.length || 0} face(s).`}
              </div>
              <button type="button"
                className="gos-btn gos-btn-teal"
                onClick={() => setShowPreviewModal(true)}>{__elakayText("t042", "👁️ Open Design Preview")}</button>
            </div>
          )}

          {sourceType === "ask" && (
            <>
              {activeTab === "request" && (
                <AskOrderViewer
                  order={order}
                  graphic={graphic}
                  product={product}
                  infoSource={infoSource}
                />
              )}

              {activeTab === "design" && (
                submitted ? (
                  <div className="gos-submitted-screen">
                    <div className="gos-submitted-icon">✅</div>
                    <div className="gos-submitted-title">{text("t027", "Design Submitted!")}</div>
                    <div className="gos-submitted-sub">{__elakayText("t043", "Your design has been submitted to the client. They will be notified and can approve or request revisions.")}</div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
                      <button type="button" className="gos-btn gos-btn-secondary"
                        onClick={() => setSubmitted(false)}>{__elakayText("t044", "✏️ Continue Editing")}</button>
                      <button type="button" className="gos-btn gos-btn-teal"
                        onClick={() => setActiveTab("request")}>{__elakayText("t045", "📋 View Request")}</button>
                    </div>
                  </div>
                ) : (
                  <div className="gos-studio-wrap">
                    {studioSlots.length > 0 ? (
                      <DesignStudio
                        infoSource={infoSource}
                        slots={studioSlots}
                        setSlots={setStudioSlots}
                        setPickerOpen={setPickerOpen}
                        setActiveSlotId={setActiveSlotId}
                        onBack={() => setActiveTab("request")}
                        onPublish={() => {}}
                        templateMeta={studioMeta}
                        onMetaChange={setStudioMeta}
                        initialDesigns={studioDesigns}
                        onDesignsChange={setStudioDesigns}
                        open={pickerOpen}
                        ImageGallery={ImageGallery}
                        images={images}
                        slotId={activeSlotId}
                      />
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, color: "var(--text-muted)", fontSize: 13 }}>⏳</div>
                    )}
                  </div>
                )
              )}
            </>
          )}

        </div>

        {/* Footer */}
        <div className="gos-footer">

          <div className="gos-footer-l">
            <div>
              <span className="gos-price-label">{text("t028", "Order Total")}</span>
              <div className="gos-price-value">${total}</div>
            </div>
            {(order?.prod_name || product) && (
              <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                {order?.prod_name || product?.name || elakayAutoText("h_dd3b86d1ef21b9", "Product")}
                {product?.variant && ` · ${product.variant}`}
                {product?.qty && ` · ×${product.qty}`}
              </div>
            )}
          </div>

          <div className="gos-footer-r">

            {sourceType === "ask" && activeTab === "request" && (
              <>
                <button type="button" className="gos-btn gos-btn-secondary" onClick={() => window.print()}>{__elakayText("t046", "🖨️ Print")}</button>
                <button type="button" className="gos-btn gos-btn-gold"
                  onClick={async () => {
                    try {
                      const { default: jsPDF } = await import("jspdf");
                      const { default: html2canvas } = await import("html2canvas");
                      const printEl = document.querySelector(".gos-print-area");
                      if (!printEl) return;

                      const canvas = await html2canvas(printEl, {
                        scale: 2, backgroundColor: "#ffffff", useCORS: true
                      });

                      const imgData = canvas.toDataURL("image/jpeg", 0.95);
                      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
                      const pdfWidth = pdf.internal.pageSize.getWidth();
                      const pdfHeight = pdf.internal.pageSize.getHeight();
                      const ratio = canvas.height / canvas.width;
                      const imgHeight = pdfWidth * ratio;

                      let position = 0;
                      let heightLeft = imgHeight;
                      pdf.addImage(imgData, "JPEG", 0, position, pdfWidth, imgHeight);
                      heightLeft -= pdfHeight;

                      while (heightLeft > 0) {
                        position = heightLeft - imgHeight;
                        pdf.addPage();
                        pdf.addImage(imgData, "JPEG", 0, position, pdfWidth, imgHeight);
                        heightLeft -= pdfHeight;
                      }

                      pdf.save(`elakay-request-${orderId}-${Date.now()}.pdf`);
                    } catch (err) {
                      alert(__elakayText("t055", "Install jspdf and html2canvas"));
                    }
                  }}>{__elakayText("t047", "📄 Download PDF")}</button>
                <button type="button" className="gos-btn gos-btn-teal"
                  onClick={() => setActiveTab("design")}>{__elakayText("t048", "🎨 Start Designing →")}</button>
              </>
            )}

            {sourceType === "ask" && activeTab === "design" && !submitted && (
              <>
                <button type="button" className="gos-btn gos-btn-ghost"
                  onClick={handleSaveDraft}
                  disabled={save.busy}>
                  {save.busy && save.busyMode === "draft" 
                    ? elakayAutoText("h_332e41e125cd6a", "⏳ Saving...") 
                    : elakayAutoText("h_0ba278608b61eb", "💾 Save Draft")}
                </button>

                <button type="button" className="gos-btn gos-btn-success"
                  onClick={handleSubmit}
                  disabled={save.busy || !canSave}>
                  {save.busy && save.busyMode === "checkout"
                    ? elakayAutoText("h_0cebd16b648ed4", "⏳ Submitting...")
                    : elakayAutoText("h_418b5243040827", "🚀 Submit to Client")}
                </button>
              </>
            )}

            <button type="button" className="gos-btn gos-btn-ghost" onClick={onClose}>
              {text("t023", "Close")}
            </button>

          </div>
        </div>

      </div>

      {showPreviewModal && reconstructedDesign && (
        <DesignPreviewModal
          design={reconstructedDesign}
          infoSource={infoSource}
          onClose={() => setShowPreviewModal(false)}
          showEditButton={false}
        />
      )}

    </>
  );
}
